import { generatePDF } from './generatePDF';  // Ensure this path matches where your generatePDF function is located

export async function handleSubmit(event, formData = {}, setSubmissionMessage, setFieldErrors, investorType = "Investor") {
  console.log("🔥 handleSubmit triggered");
  event.preventDefault();

  const formElement = event.target;
  const errors = {};
  let firstErrorField = null;

  const partIVSelections = [
    formData.entityInvestmentAdvisers.entityInvests25M,
    formData.entityInvestmentAdvisers.trustQualifiedPurchaser,
    formData.entityInvestmentAdvisers.familyCompany,
    formData.entityInvestmentAdvisers.companyQualifiedPurchaser,
    formData.entityInvestmentAdvisers.qualifiedInstitutionalBuyer
  ];

  try {
    const requiredFields = Array.from(formElement.querySelectorAll("[required]"))
      .map(input => input.name)
      .filter(Boolean);

    requiredFields.forEach(field => {
      const input = formElement.querySelector(`[name="${field}"]`);
      const value = formData?.[field];

      // console.log(`🔍 Checking field: ${field}`, {
      //   domValue: input?.value,
      //   formDataValue: value
      // });

      if (input?.type === "checkbox" && !input.checked) {
        errors[field] = "❗ Please check this box if you want to proceed.";
        if (!firstErrorField) firstErrorField = field;
      } else if (typeof value === "string" && value.trim() === "") {
        errors[field] = "❗ This field is required.";
        if (!firstErrorField) firstErrorField = field;
      } else if (Array.isArray(value) && value.length === 0) {
        errors[field] = "❗ Please select at least one option.";
        if (!firstErrorField) firstErrorField = field;
      } else if (
        typeof value === "object" &&
        value !== null &&
        !Object.values(value).some(v => v === true)
      ) {
        errors[field] = "❗ Please check at least one option.";
        if (!firstErrorField) firstErrorField = field;
      }
    });

    // 🔒 Logical Consistency Check
    const qualPurchaser = formData?.investmentAdvisers?.qualPurchaser;
    const ownsFiveMil = formData?.qualifiedPurchaser?.ownsFiveMil;

    if ((qualPurchaser && ownsFiveMil) && qualPurchaser !== ownsFiveMil) {
      errors["investmentAdvisers.qualPurchaser"] = "❗ This answer must match your response in Part III.";
      errors["qualifiedPurchaser.ownsFiveMil"] = "❗ This answer must match your response in Part II(c).";
      if (!firstErrorField) firstErrorField = "qualifiedPurchaser.ownsFiveMil";
    }

    // 🔒 At least one checkbox in accreditedInvestorStatus must be true
    const accStatus = formData?.accreditedInvestorStatus;
    if (investorType !== "entity") {
    if (accStatus && typeof accStatus === 'object' && !Object.values(accStatus).some(Boolean)) {
      errors.accreditedInvestorStatus = "❌ Unfortunately, the sponsor is not currently able to accept investors that are not accredited. If you selected this response in error, please re-read the question. If your response was not an error, please contact the sponsor. Do not submit any inaccurate information.";
      if (!firstErrorField) firstErrorField = "accreditedInvestorStatus";
      }
    }

    // 🔒 At least one checkbox in entityAccreditedInvestor must be true
    const entityAccStatus = formData?.entityAccreditedInvestor;
    if (investorType === "entity") {
    if (entityAccStatus && typeof entityAccStatus === 'object' && !Object.values(entityAccStatus).some(Boolean)) {
      errors.entityAccreditedInvestor = "❌ Unfortunately, the sponsor is not currently able to accept investors that are not accredited. If you selected this response in error, please re-read the question. If your response was not an error, please contact the sponsor. Do not submit any inaccurate information.";
      if (!firstErrorField) firstErrorField = "entityAccreditedInvestor";
      }
    }

    // Custom logic: at least one checkbox in entityInvestmentAdvisers must be checked
    const entityAdviserValues = formData?.entityInvestmentAdvisers
    ? Object.entries(formData.entityInvestmentAdvisers)
        .filter(([key]) => key !== "notQualifiedPurchaser") // ignore opt-out
        .map(([, value]) => value)
    : [];

    const hasEntityAdviserSelection = entityAdviserValues.some((v) => v === true || v === "true");

    if (investorType === "entity" && !hasEntityAdviserSelection) {
    errors.entityInvestmentAdvisers = "❌ Unfortunately, the sponsor is not currently able to accept investors that are not qualified clients. If you selected this response in error, please re-read the question. If your response was not an error, please contact the sponsor. Do not submit any inaccurate information.";
    if (!firstErrorField) firstErrorField = "entityInvestmentAdvisers";
    }
    
    
    // 🔒 Disqualifying Event must be "true" (individual)
    if (investorType === "individual" && formData.disqualifyingEvent !== 'true') {
      errors.disqualifyingEvent = "❌ Unfortunately, the sponsor is not currently able to accept investors that select this answer. If you selected this response in error, please re-read the question. If your response was not an error, please contact the sponsor. Do not submit any inaccurate information.";
      if (!firstErrorField) firstErrorField = "disqualifyingEvent";
    }

    // 🔒 Entity Disqualifying Event must be "true" (entity)
    if (investorType === "entity" && formData.entityDisqualifyingEvent !== 'true') {
      errors.entityDisqualifyingEvent = "❌ Unfortunately, the sponsor is not currently able to accept investors that select this answer. If you selected this response in error, please re-read the question. If your response was not an error, please contact the sponsor. Do not submit any inaccurate information.";
      if (!firstErrorField) firstErrorField = "entityDisqualifyingEvent";
    }

    // 🔒 Require Signature Individual Form (either ind or entity)
    if (investorType === "individual") {
      const individualSig = formData.signature || formData.signatureEntityInd;
      if (!individualSig || individualSig.trim() === "") {
        errors.signature = "❗ Signature is required.";
        if (!firstErrorField) firstErrorField = "signature";
      }
    }

    // 🔒 Require Signature (Spouse)
    if (investorType === "individual" && formData.miscMatters.representType === "jointTenancy") {
      const spouseSig = formData.signatureSpouse;
      if (!spouseSig || spouseSig.trim() === "") {
        errors.signatureSpouse = "❗ Spouse signature is required.";
        if (!firstErrorField) firstErrorField = "signatureSpouse";
      }
    }


    if (investorType === "entity") {
      const entitySig = formData.signatureEntity;
      if (!entitySig || entitySig.trim() === "") {
        errors.signatureEntity = "❗ Signature is required.";
        if (!firstErrorField) firstErrorField = "signatureEntity";
      }
    }

    // Error if any IV(1)-(5) are true AND III(b) is false
    if (
      investorType === "entity" &&
      partIVSelections.some((val) => val === true) &&
      formData.entityInvestmentDetails.qualifiedClientNetWorth === 'false'
    ) {
      errors.entityInvestmentAdvisers = "❌ You mentioned you are not a qualified purchaser pursuant to Part III(b). Please revise this answer or your answer to Part III(b).";
      if (!firstErrorField) firstErrorField = "entityInvestmentDetails";
    }

    if (Object.keys(errors).length > 0) {
      console.warn("❌ Validation failed", errors);
      setFieldErrors(errors);
      let errorElement = document.querySelector(`[name="${firstErrorField}"]`);

      if (!errorElement && firstErrorField) {
        const fallbackId = `${firstErrorField}ScrollAnchor`;
        errorElement = document.getElementById(fallbackId) || document.getElementById(`${firstErrorField}Fieldset`);
      }
      
      if (errorElement) {
        // ⏳ Delay scroll just long enough for DOM to paint
        setTimeout(() => {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          errorElement.focus({ preventScroll: true });
        }, 50);
      }      
      return;
    }

    setFieldErrors({});
    setSubmissionMessage("✅ Generating PDF...");

   // Generate the PDF and download it
    const pdfBlob = await generatePDF(formData, investorType); // Adjust generatePDF to return the Blob
    const pdfBlobUrl = URL.createObjectURL(pdfBlob);

    // ✅ Dynamic, investor-type-aware filename
    const isEntity = investorType.toLowerCase() === "entity";
    const isJoint = investorType.toLowerCase() === "jointspouse";

    // Choose name field based on type and content
    const name = isEntity
      ? formData.subscriberNameEntity
      : isJoint
        ? formData.jointSubscriber
        : formData.subscriberName || formData.subscriberNameEntityInd || "Unknown";

    const sanitizedName = name.trim().replace(/\s+/g, "_");

    const date = isEntity
      ? formData.selectedDateEntity || 'DATE'
      : formData.selectedDate || 'DATE';

    const fileName = `XEIA_${investorType}_IQS_${sanitizedName}_${date}.pdf`;

    const a = document.createElement("a");
    a.href = pdfBlobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setSubmissionMessage("✅ PDF generated and downloaded.");
    return { success: true, message: "✅ PDF generated successfully. Please check your downloads folder." };

  } catch (err) {
    console.error("❌ Submission Error:", err);
    setSubmissionMessage("❌ An error occurred during submission.");
    return { success: false, message: "❌ Submission failed. Try again." };
  }
}
